import React, { useState, useEffect, useRef } from 'react';
import { useBookmarks } from '../context/BookmarkContext';
import { generateLearningNugget } from '../services/api';

function LernenPage() {
  const { getBookmarks, removeBookmark, updateBookmark, seedDemoNuggets } = useBookmarks();
  const bookmarks = getBookmarks();
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [errorIds, setErrorIds] = useState(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const triggeredRef = useRef(new Set());

  // Auto-generate learning content for new bookmarks
  useEffect(() => {
    bookmarks.forEach((bookmark) => {
      if (!bookmark.learningContent && !loadingIds.has(bookmark.id) && !triggeredRef.current.has(bookmark.id)) {
        triggeredRef.current.add(bookmark.id);
        fetchLearningContent(bookmark);
      }
    });
  }, [bookmarks]);

  // Auto-select first bookmark if none selected or selected was deleted
  useEffect(() => {
    const filtered = getFilteredBookmarks();
    if (filtered.length > 0 && (!selectedId || !filtered.find((b) => b.id === selectedId))) {
      setSelectedId(filtered[0].id);
    }
    if (filtered.length === 0) {
      setSelectedId(null);
    }
  }, [bookmarks, searchQuery]);

  const fetchLearningContent = async (bookmark) => {
    setLoadingIds((prev) => new Set(prev).add(bookmark.id));
    setErrorIds((prev) => {
      const next = new Set(prev);
      next.delete(bookmark.id);
      return next;
    });

    try {
      const topic = `${bookmark.sectionLabel}: ${bookmark.itemText}`;
      const context = `Im Kontext der Diagnose: ${bookmark.diagnosisName}`;
      const content = await generateLearningNugget(topic, context);
      updateBookmark(bookmark.id, { learningContent: content });
    } catch (err) {
      console.error('Learning nugget generation failed:', err);
      setErrorIds((prev) => new Set(prev).add(bookmark.id));
      triggeredRef.current.delete(bookmark.id);
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(bookmark.id);
        return next;
      });
    }
  };

  const getFilteredBookmarks = () => {
    if (!searchQuery.trim()) return bookmarks;
    const q = searchQuery.toLowerCase();
    return bookmarks.filter((b) => {
      const title = b.learningContent?.title || '';
      const sectionTexts = (b.learningContent?.sections || []).map((s) => {
        let text = (s.title || '') + ' ' + (s.content || '');
        if (s.items) {
          text += ' ' + s.items.map((item) => (typeof item === 'string' ? item : `${item.key} ${item.value}`)).join(' ');
        }
        return text;
      }).join(' ');
      const searchable = `${title} ${b.diagnosisName} ${b.sectionLabel} ${b.itemText} ${sectionTexts}`.toLowerCase();
      return searchable.includes(q);
    });
  };

  const handleDelete = (id) => {
    removeBookmark(id);
    triggeredRef.current.delete(id);
    setDeleteConfirmId(null);
  };

  const getDisplayName = (bookmark) => {
    if (bookmark.learningContent?.title) return bookmark.learningContent.title;
    const text = bookmark.itemText;
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  };

  const renderSection = (section, i) => {
    switch (section.type) {
      case 'info':
        return (
          <div key={i} className="nugget-section nugget-info">
            <h4>{section.title}</h4>
            <p>{section.content}</p>
          </div>
        );
      case 'keyvalue':
        return (
          <div key={i} className="nugget-section nugget-keyvalue">
            <h4>{section.title}</h4>
            <div className="nugget-kv-grid">
              {(section.items || []).map((kv, j) => (
                <div key={j} className="nugget-kv-row">
                  <span className="nugget-kv-key">{kv.key}</span>
                  <span className="nugget-kv-value">{kv.value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'warning':
        return (
          <div key={i} className="nugget-section nugget-warning">
            <h4>{section.title}</h4>
            <p>{section.content}</p>
          </div>
        );
      case 'checklist':
        return (
          <div key={i} className="nugget-section nugget-checklist">
            <h4>{section.title}</h4>
            <ul>
              {(section.items || []).map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          </div>
        );
      case 'text':
      default:
        return (
          <div key={i} className="nugget-section nugget-text">
            <h4>{section.title}</h4>
            <p>{section.content}</p>
          </div>
        );
    }
  };

  const filteredBookmarks = getFilteredBookmarks();
  const selectedBookmark = bookmarks.find((b) => b.id === selectedId);

  return (
    <div className="page page-wide">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Lernen</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Vertiefen Sie Ihr Wissen zu markierten Themen (Ziel ist es, 
            diese Lerninhalte in späteren Versionen auch an Fotbildungskurse für CME-Punkte anzulehnen. 
            So kann man gezielt für bestimmte Themen an eigenen Fallbeispielen relevantes neues Wissen 
            für die eigene Fortbildung und den Erwerb von CME-Punkten aufbauen.)</p>
        </div>
        <button
          onClick={seedDemoNuggets}
          style={{ padding: '8px 14px', fontSize: 13, border: '1.5px dashed #c4b5fd', background: '#faf5ff', color: '#7c3aed', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', marginTop: 4 }}
          title="10 Lernkarten aus den Demo-Patienten laden"
        >
          + Demo-Lernkarten laden
        </button>
      </div>

      {bookmarks.length === 0 ? (
        <div className="session-list-empty">
          <p>Keine Lern-Bookmarks vorhanden.</p>
          <p className="session-list-empty-hint">
            Markieren Sie Aspekte in den Differentialdiagnosen mit dem Bookmark-Symbol,
            um hier vertiefende Inhalte zu erhalten.
          </p>
        </div>
      ) : (
        <div className="learning-layout">
          {/* Sidebar */}
          <div className="learning-sidebar">
            <div className="learning-nav-search-wrapper">
              <svg className="learning-nav-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="learning-nav-search"
                placeholder="Suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="learning-nav-list">
              {filteredBookmarks.length === 0 ? (
                <div className="learning-nav-empty">Keine Ergebnisse</div>
              ) : (
                filteredBookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className={`learning-nav-item${selectedId === bookmark.id ? ' active' : ''}`}
                    onClick={() => setSelectedId(bookmark.id)}
                  >
                    <div className="learning-nav-item-content">
                      <span className="learning-nav-item-name">
                        {loadingIds.has(bookmark.id) && (
                          <span className="learning-nav-spinner" />
                        )}
                        {getDisplayName(bookmark)}
                      </span>
                      <span className="learning-nav-item-diagnosis">({bookmark.diagnosisName})</span>
                    </div>
                    <button
                      className="learning-nav-item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(bookmark.id);
                      }}
                      title="Merker entfernen"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="learning-detail">
            {selectedBookmark ? (
              <>
                <div className="learning-detail-header">
                  <span className="learning-card-diagnosis">{selectedBookmark.diagnosisName}</span>
                  <span className="learning-card-section-label">{selectedBookmark.sectionLabel}</span>
                  <p className="learning-detail-item">{selectedBookmark.itemText}</p>
                </div>

                <div className="learning-detail-body">
                  {loadingIds.has(selectedBookmark.id) ? (
                    <div className="learning-loading">
                      <div className="learning-spinner" />
                      <span>Lernkarte wird generiert...</span>
                    </div>
                  ) : errorIds.has(selectedBookmark.id) ? (
                    <div className="learning-error">
                      <p>Fehler beim Generieren der Lernkarte.</p>
                      <button
                        className="learning-retry-btn"
                        onClick={() => fetchLearningContent(selectedBookmark)}
                      >
                        Erneut versuchen
                      </button>
                    </div>
                  ) : selectedBookmark.learningContent ? (
                    <div className="learning-nugget">
                      <h3 className="nugget-title">{selectedBookmark.learningContent.title}</h3>
                      {(selectedBookmark.learningContent.sections || []).map((section, i) =>
                        renderSection(section, i)
                      )}
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="learning-detail-empty">
                <p>Wählen Sie einen Bookmark aus der Liste aus.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {deleteConfirmId && (
        <div className="session-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Merker entfernen</h3>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Möchten Sie diesen Bookmark und den zugehörigen Lerninhalt wirklich entfernen?
            </p>
            <div className="session-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setDeleteConfirmId(null)}>
                Abbrechen
              </button>
              <button className="modal-btn-confirm" onClick={() => handleDelete(deleteConfirmId)} style={{ background: '#dc2626' }}>
                Entfernen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LernenPage;
